import { ABORT_ERROR_CODE, CLIENT_ERROR_CODE, NETWORK_ERROR_CODE } from '@app/constants/Misc'

import { isAbortError } from '@app/errors/AbortError'
import type { FetchDataErrorMessage } from '@app/errors/FetchDataError'
import { createFetchDataError } from '@app/errors/FetchDataError'

import { asNetworkError, isNetworkFailError } from '@app/packages/api/errors'
import { asError } from '@app/packages/asError/asError'

import type { StoreDispatch } from '@app/store/dispatch'
import { createThunk } from '@app/store/thunk'

type ApiActionErrorOptions = {
  expose?: boolean
  handled?: boolean
}

/**
 * Localizes a failed api call as a FetchDataError the route error boundary can render. The
 * message may be chosen from the error, for a status that means something to the screen.
 */
export const catchFetchDataError =
  (
    dispatch: StoreDispatch,
    errorString: FetchDataErrorMessage | ((error: Error) => FetchDataErrorMessage),
    options?: ApiActionErrorOptions | ((error: Error) => ApiActionErrorOptions)
  ) =>
  (e: unknown): never => {
    const error = asError(e)
    throw dispatch(errorToFetchDataError(error, typeof errorString === 'function' ? errorString(error) : errorString, options))
  }

export const errorToFetchDataError = (
  error: Error,
  errorString: FetchDataErrorMessage,
  options?: ApiActionErrorOptions | ((error: Error) => ApiActionErrorOptions)
) =>
  createThunk(dispatch => {
    const err = dispatch(
      createFetchDataError(
        errorString,
        isAbortError(error) ? ABORT_ERROR_CODE : isNetworkFailError(error) ? NETWORK_ERROR_CODE : (asNetworkError(error)?.status ?? CLIENT_ERROR_CODE)
      )
    ).withCause(error)
    const opts = (typeof options === 'function' ? options(error) : options) ?? {}
    for (const key of Reflect.ownKeys(opts)) {
      err[key] = opts[key]
    }
    err.handled = opts.handled ?? (error as any).handled ?? isNetworkFailError(error)
    err.expose = opts.expose ?? isNetworkFailError(error)
    return err
  })
